/**
 * Downloads a file through the page, so its progress can be shown (the ring around a file's
 * download button in Messages, Joey, Oct 29 2026). The file is read as it arrives, then handed to
 * the browser (or the Mac and Windows apps) as a normal download, with its own name.
 *
 * Very big files, and browsers that can't read a download as it arrives, go the browser's own way
 * (no ring): holding a 500 MB file in the page would weigh on a phone or an older computer.
 */

/** Files up to this size are read by the page (with progress); bigger ones go the browser's way. */
export const PAGE_DOWNLOAD_MAX_BYTES = 300 * 1024 * 1024;

export interface DownloadProgress {
  /** Bytes so far. */
  received: number;
  /** The whole file, when known (else from the card's own size, else null). */
  total: number | null;
}

/** The browser's own download (a link click): no progress. */
export function nativeDownload(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Whether this browser can read a download as it arrives. */
export function canTrackDownloads(): boolean {
  return typeof fetch === 'function' && typeof ReadableStream !== 'undefined' && typeof URL.createObjectURL === 'function';
}

/**
 * Reads `url` with progress, then saves it as `name`. Resolves when the browser has been given the
 * file; rejects with an AbortError when `signal` aborts (the person cancelled), or an Error saying
 * what went wrong.
 */
export async function downloadWithProgress(
  url: string,
  name: string,
  opts: { bytes?: number | null; signal?: AbortSignal; onProgress?: (p: DownloadProgress) => void } = {}
): Promise<void> {
  const res = await fetch(url, { credentials: 'same-origin', signal: opts.signal ?? null });
  if (!res.ok) throw new Error(res.status === 404 ? 'That file isn’t here any more.' : 'The file didn’t download. Try again.');
  const header = Number(res.headers.get('content-length'));
  const total = Number.isFinite(header) && header > 0 ? header : (opts.bytes ?? null);
  const type = res.headers.get('content-type') ?? 'application/octet-stream';
  const chunks: BlobPart[] = [];
  let received = 0;
  opts.onProgress?.({ received, total });
  if (res.body) {
    const reader = res.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      opts.onProgress?.({ received, total });
    }
  } else {
    const all = await res.arrayBuffer();
    chunks.push(all);
    received = all.byteLength;
    opts.onProgress?.({ received, total });
  }
  const blob = new Blob(chunks, { type });
  const href = URL.createObjectURL(blob);
  try {
    nativeDownload(href, name);
  } finally {
    // The browser has its copy once the click is handled; a minute is plenty, even on a slow disk.
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  }
}

/** 0 to 1, or null when the size isn't known. */
export function fractionOf(p: DownloadProgress | null): number | null {
  if (!p || !p.total) return null;
  return Math.max(0, Math.min(1, p.received / p.total));
}
